import { useDetailPreview } from '@/contexts/DetailPreviewContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/i18n/language-hooks';
import { cn } from '@/lib/utils';
import { ArrowUpRight, X } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Sliding details panel beside <main> on desktop. Mobile list pages navigate to
 * full-size detail routes instead of using this aside.
 */
export const DetailAside = () => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const { preview, closePreview } = useDetailPreview();

  if (isMobile || !preview) return null;

  const handleClose = () => {
    preview.onClose?.();
    closePreview();
  };

  const fullViewLink = preview.fullViewHref ? (
    <Link
      to={preview.fullViewHref}
      onClick={handleClose}
      className='text-fin-gray-400 hover:bg-fin-gray-800 hover:text-fin-white rounded-md p-1.5 transition-colors'
      aria-label={t('detail.openFullView')}
      title={t('detail.openFullView')}
    >
      <ArrowUpRight className='size-4' aria-hidden />
    </Link>
  ) : null;

  const hasHeaderTitle = preview.title.trim().length > 0;

  return (
    <aside
      aria-label={hasHeaderTitle ? preview.title : t('trade.ticket.title')}
      className={cn(
        'bg-card animate-in slide-in-from-right-4 fade-in md:border-fin-gray-800 flex w-full shrink-0 flex-col overflow-hidden duration-200 md:rounded-xl md:border md:shadow-sm',
        preview.size === 'md' ? 'max-w-md' : 'max-w-sm'
      )}
    >
      <div
        className={cn(
          'flex items-start justify-between gap-2 px-4 py-3',
          hasHeaderTitle && 'border-fin-gray-800 border-b'
        )}
      >
        <div className='min-w-0 flex-1'>
          {hasHeaderTitle ? (
            <>
              <h2 className='text-fin-white truncate text-sm font-semibold'>{preview.title}</h2>
              {preview.subtitle ? (
                <p className='text-muted-foreground truncate text-xs'>{preview.subtitle}</p>
              ) : null}
            </>
          ) : null}
        </div>
        <div className='flex shrink-0 items-center gap-0.5'>
          {fullViewLink}
          <button
            type='button'
            onClick={handleClose}
            className='text-fin-gray-400 hover:bg-fin-gray-800 hover:text-fin-white rounded-md p-1.5 transition-colors'
            aria-label={t('common.cancel')}
          >
            <X className='size-4' aria-hidden />
          </button>
        </div>
      </div>
      <div className='min-h-0 flex-1 overflow-y-auto p-4'>{preview.content}</div>
    </aside>
  );
};

export default DetailAside;
